import { Button, CodeBox, Label } from '@localina/core';
import { Language, QrCode2 } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { usePDF } from 'react-to-pdf';
import { useRestaurantId } from '../../../utils/RestaurantUtils';

const COPIED_FEEDBACK_MS = 3000;

const MenuIntegrations = () => {
    const { t } = useTranslation();
    const restaurantId = useRestaurantId();
    const [linkCopied, setLinkCopied] = React.useState(false);

    React.useEffect(() => {
        if (!linkCopied) {
            return;
        }
        const timeout = setTimeout(() => setLinkCopied(false), COPIED_FEEDBACK_MS);
        return () => clearTimeout(timeout);
    }, [linkCopied]);

    const menuLink = `${window.location.origin}/widget/?id=${restaurantId}&menu`;
    const integrationScript =
        `<iframe \n` +
        `loading="lazy" id="inline-frame-restaurant-menu" name="Inline Frame Restaurant Menu" width="100%" height="500px" style="border: none; overflow: auto;" ` +
        ` src="${menuLink}">\n` +
        '</iframe>';

    const handleCopyLink = async () => {
        await navigator.clipboard.writeText(menuLink);
        setLinkCopied(true);
    };

    const { toPDF, targetRef } = usePDF({ filename: 'daily-menu-qr-code.pdf' });
    const handleDownloadQRCode = () => {
        toPDF();
    };

    return (
        <div className={'menu-integrations-tab-container'}>
            <section className="menu-card">
                <div className="menu-card__head">
                    <span className="menu-card__icon">
                        <Language />
                    </span>
                    <h3>{t('integrations.items.menu.tabs.integrations.htmlIntegrationTitle')}</h3>
                </div>
                <div className="menu-card__body">
                    <p className="menu-card__hint">
                        {t('integrations.items.menu.tabs.integrations.htmlIntegrationHint')}
                    </p>
                    <CodeBox
                        value={integrationScript}
                        ariaLabel={t('integrations.items.menu.tabs.integrations.htmlIntegrationLabel')}
                        copyLabel={t('integrations.items.menu.tabs.integrations.buttons.copyHtml')}
                        copiedLabel={t('integrations.items.menu.tabs.integrations.feedback')}
                    />
                </div>
            </section>
            <section className="menu-card">
                <div className="menu-card__head">
                    <span className="menu-card__icon">
                        <QrCode2 />
                    </span>
                    <h3>{t('integrations.items.menu.tabs.integrations.qrCodeTitle')}</h3>
                </div>
                <div className="menu-card__body">
                    <p className="menu-card__hint">{t('integrations.items.menu.tabs.integrations.qrCodeHint')}</p>
                    <div className={'qrcode-container'}>
                        <QrCodeComponent link={menuLink} />
                    </div>
                    <div className={'qrcode-container-to-download'} style={{ position: 'absolute', left: '-500%' }}>
                        <QrCodeComponent ref={targetRef} link={menuLink} />
                    </div>
                    <div className="menu-card__actions">
                        <Button
                            label={
                                <span aria-live="polite">
                                    {linkCopied
                                        ? t('integrations.items.qrcode.feedback')
                                        : t('integrations.items.menu.tabs.integrations.buttons.copyLink')}
                                </span>
                            }
                            secondary
                            onClick={handleCopyLink}
                        />
                        <Button
                            label={t('integrations.items.menu.tabs.integrations.buttons.downloadQrCode')}
                            secondary
                            onClick={handleDownloadQRCode}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

interface IQrCodeComponentProps {
    link: string;
}

const QrCodeComponent = React.forwardRef<HTMLDivElement, IQrCodeComponentProps>((props, ref) => {
    const { t } = useTranslation();
    return (
        <div className={'qrcode-wrapper'} id="qrcode-wrapper" ref={ref}>
            <div className="qrcode">
                <QRCode id="qrcode" level="H" size={300} value={props.link} fgColor={'#003a5c'} />
            </div>

            <div className="qrcode-subtitle">
                <Label type={'text'} value={t('integrations.items.menu.tabs.integrations.qrCodeSubtitle')} />
            </div>
        </div>
    );
});

export default MenuIntegrations;
